import useDarkMode from '@rio-cloud/rio-uikit/useDarkMode';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { getUserAccount } from '../../configuration/login/loginSlice';
import { getTrackingAttributes } from '../../configuration/setup/googleAnalytics';
import { useAppSelector } from '../../configuration/setup/hooks';
import { getTenant, isLatamTenantValue } from '../../configuration/tokenHandling/tokenSlice';
import { useData } from '../../hooks/useData';
import { isFleetMonitorVisibleForAccount, isFleetMonitorVisibleForTenant } from '../../services/serviceVisibility';

const QuickstartNavigation = () => {
    const isDarkMode = useDarkMode();
    const { data, error } = useData();
    const tenant = useAppSelector(getTenant);
    const account = useAppSelector(getUserAccount);
    const showMaintenanceRepairMonitor = isLatamTenantValue(tenant);
    const showFleetMonitor = isFleetMonitorVisibleForTenant(tenant) && isFleetMonitorVisibleForAccount(account);

    // Wait until the data is loaded to avoid flickering and stretching the items
    // in case the pages are not rendered
    if (!data && !error) {
        return null;
    }

    const quickstartClassName = 'display-flex justify-content-between gap-10';
    const quickstartIconClassName = 'aspect-ratio-1 width-40 bg-size-cover margin-x-15';
    const QuickstartCaptionClassName = classNames(
        'line-height-125rel',
        'margin-top-5',
        'text-color-darker',
        'text-size-12'
    );
    const quickstartTileClassName = classNames(
        'align-items-center',
        'cursor-pointer',
        'display-flex',
        'flex-column',
        'padding-bottom-10',
        'padding-top-15',
        'rounded',
        'text-center',
        'text-decoration-none',
        'width-50pct',
        'border',
        isDarkMode ? 'bg-lightest hover-border-color-highlight-lightest' : 'border-color-lighter hover-bg-lightest'
    );

    return (
        <div className={quickstartClassName}>
            <a
                className={quickstartTileClassName}
                href='https://home.rio.cloud/'
                target='_top'
                rel='noreferrer'
                {...getTrackingAttributes({ trigger: 'click', action: 'quickStartNavigatedTo::home' })}
            >
                <div
                    className={quickstartIconClassName}
                    style={{
                        backgroundImage: 'url("https://rio.cloud/fileadmin/Marketplace/AppIcons/Home.svg")',
                    }}
                />
                <div className={QuickstartCaptionClassName}>
                    <FormattedMessage id={'intl-msg:glossary.serviceName.home'} />
                </div>
            </a>
            {showMaintenanceRepairMonitor ? (
                <a
                    className={quickstartTileClassName}
                    href='https://latam-uptime-gateway.rio.cloud/'
                    target='_top'
                    rel='noreferrer'
                    {...getTrackingAttributes({
                        trigger: 'click',
                        action: 'quickStartNavigatedTo::latam-uptime-gateway',
                    })}
                >
                    <div
                        className={quickstartIconClassName}
                        style={{
                            backgroundImage:
                                'url("https://rio.cloud/fileadmin/Marketplace/AppIcons/LATAM-Uptime-Gateway.svg")',
                        }}
                    />
                    <div className={QuickstartCaptionClassName}>
                        <FormattedMessage id={'intl-msg:glossary.serviceName.latam-uptime-gateway'} />
                    </div>
                </a>
            ) : showFleetMonitor ? (
                <a
                    className={quickstartTileClassName}
                    href='https://livemonitor.rio.cloud/'
                    target='_top'
                    rel='noreferrer'
                    {...getTrackingAttributes({ trigger: 'click', action: 'quickStartNavigatedTo::fleetmonitor' })}
                >
                    <div
                        className={quickstartIconClassName}
                        style={{
                            backgroundImage:
                                'url("https://rio.cloud/fileadmin/Marketplace/AppIcons/FleetmonitorQuickstart.svg")',
                        }}
                    />
                    <div className={QuickstartCaptionClassName}>
                        <FormattedMessage id={'intl-msg:glossary.serviceName.fleetmonitor'} />
                    </div>
                </a>
            ) : null}
        </div>
    );
};

export default QuickstartNavigation;
